/**
 * 全站標籤篩選器（只用於首頁 feed）。
 *
 * 觸發方式：勾選 / 取消 tag 時只更新 URL 的 ?tags 參數，
 * 實際撈資料交由 HomeLayout 的 searchParams useEffect「單一來源」處理，
 * 元件本身不直接 callback，避免同一次點擊打兩次 API。
 *
 * variant：
 *   'desktop' — 直接內嵌 checkbox 清單（放在左側欄，由容器 d-none d-lg-block 控制顯示）
 *   'mobile'  — 按鈕開 MessageModal（放在主欄，由容器 d-lg-none 控制顯示）
 */
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import MessageModal from "@/components/ui/MessageModal";

const Hashtag = ({ variant = 'desktop', tagsData, refreshKey }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const modalFilter = useRef(null);
    const [selectedTags, setSelectedTags] = useState([]);

    // 勾選 / 取消 tag：只更新 URL，撈資料交給 HomeLayout 監聽 searchParams 處理
    const toggleTag = (tagId) => {
        const id = tagId.toString();
        const updatedTags = selectedTags.includes(id)
            ? selectedTags.filter(t => t !== id)
            : [...selectedTags, id];

        setSelectedTags(updatedTags);

        const tagsString = updatedTags.join(',');
        const queryParams = new URLSearchParams(location.search);
        if (tagsString) {
            queryParams.set('tags', tagsString);
        } else {
            queryParams.delete('tags');
            queryParams.delete('tag'); // 一併清掉舊的單數參數
        }

        const newSearch = queryParams.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    };

    // 監聽 URL 參數變化，同步 selectedTags 勾選狀態（含初始化）
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const urlTags = queryParams.get('tags') || queryParams.get('tag'); // 也支援舊的單數形式
        const urlTagIds = urlTags ? urlTags.split(',') : [];

        if (urlTagIds.length > 0 && tagsData) {
            // 只勾選存在於 tagsData 中的標籤
            const availableTagIds = tagsData.map(tag => tag.id.toString());
            setSelectedTags(urlTagIds.filter(id => availableTagIds.includes(id)));
        } else {
            setSelectedTags([]);
        }
    }, [location.search, tagsData]);

    const heading = (
        <>
            <FontAwesomeIcon icon={["fas", "hashtag"]} className="me-2 text-primary" width="20px" />
            {t('hashtag')}
        </>
    );

    // 桌機 / 手機共用的 checkbox 清單
    const renderTagList = (idPrefix = '') => {
        if (!tagsData || tagsData.length === 0) {
            return <div>{t('no_data')}</div>;
        }
        return (
            <div key={`${idPrefix}${refreshKey}`} className="rounded p-3 border bg-white shadowA">
                {tagsData.map(tag => {
                    const inputId = `${idPrefix}${tag.id}`;
                    return (
                        <div key={`${inputId}-${refreshKey}`} className="mb-2 form-check d-flex align-items-start">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id={inputId}
                                checked={selectedTags.includes(tag.id.toString())}
                                onChange={() => toggleTag(tag.id)}
                            />
                            <label className="form-check-label ms-2" htmlFor={inputId}>
                                {tag.name} ({tag.usage_count})
                            </label>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (variant === 'mobile') {
        return (
            <>
                <div className="post-filter-button cursor-p mt-3" onClick={() => modalFilter.current.show()}>
                    {heading}
                </div>
                <MessageModal ref={modalFilter} size="md">
                    <h5 className="mb-3">{heading}</h5>
                    {renderTagList('mobile-')}
                </MessageModal>
            </>
        );
    }

    return (
        <>
            <h5 className="mb-3">{heading}</h5>
            {renderTagList()}
        </>
    );
};

Hashtag.propTypes = {
    variant: PropTypes.oneOf(['desktop', 'mobile']),
    tagsData: PropTypes.array,
    refreshKey: PropTypes.number,
};

export default Hashtag;
